import type { PointerEvent } from 'react'

import { cn } from '@/utils'
import type { HighlightBox } from './highlighter'
import { LoadingPages } from './LoadingPages'
import { StatementStrip, type StatementStripProps } from './StatementStrip'
import { useSelectKeyHeld } from './useSelectKeyHeld'

type PagesPaneProps = StatementStripProps & {
  highlight: HighlightBox | null
  loading: boolean
  onPressPages: (event: PointerEvent<HTMLElement>) => void
}

const NO_PAGES_WANTED = () => {}

// The pages pane: every page in the project as one strip, with its scrubber above it.
export const PagesPane = ({
  highlight,
  loading,
  onPressPages,
  ...strip
}: PagesPaneProps) => {
  const { folded, statements } = strip
  const boxReady = useSelectKeyHeld()

  return (
    <div
      data-pages-pane
      onPointerDown={onPressPages}
      className={cn(
        'flex min-h-0 flex-auto select-none flex-col',
        !folded && 'bg-neutral-100 pt-3',
        boxReady && 'cursor-crosshair',
      )}
    >
      {loading ? <LoadingPages /> : null}

      {!loading && statements.length === 0 ? (
        <p className="px-4 py-3 text-sm text-ink-subtle">
          No pages in this project yet.
        </p>
      ) : null}

      {statements.length === 0 ? null : (
        <StatementStrip
          {...strip}
          // Folded, no page is on screen, so none is worth fetching.
          onPagesWanted={folded ? NO_PAGES_WANTED : strip.onPagesWanted}
        />
      )}

      {/* Fixed, so it cannot grow the strip's scroll width. It would need rewriting if this
      editor ever sits inside something transformed, like a dialog. */}
      {highlight ? (
        <div
          data-highlighter
          style={highlight}
          className="pointer-events-none fixed z-30 rounded-md border border-blue-600 bg-blue-500/15 shadow-[0_0_0_1px_rgba(255,255,255,0.8)]"
        />
      ) : null}
    </div>
  )
}
